import { Assignment } from "../../../interfaces/IAssignment";
import AccordionSetup from "../AccordionSetup";
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AddCircleOutlineOutlined from '@mui/icons-material/AddCircleOutlineOutlined';
import { EventDto } from "../../../interfaces/IEvent";
import { useContext, useEffect, useState } from "react";
import { Box, Collapse, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { EbAuthenticatedAppContext } from "@eidsivabredband/app-framework";
import { enqueueSnackbar } from "notistack";
import { LoadingIndicator, SafeLink } from "@eidsivabredband/react-component-ui-package";
import { AssignmentChip } from "../../../components/Chip/EventChip";
import { CheckOutlined, CloseOutlined, DeleteOutlined, ExpandLessOutlined, ExpandMoreOutlined, NotificationsActiveOutlined } from "@mui/icons-material";
import CreateOrAddAssignment from "./CreateOrAddAssignment";

interface AssignmentContainerProps {
    viewEvent: EventDto
    onUpdateEventView: (eventId: string) => Promise<void | EventDto>;
}

function AssignmentContainer({viewEvent, onUpdateEventView}: AssignmentContainerProps) {
    const { context } = useContext(EbAuthenticatedAppContext)
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [openAssignmentDialog, setOpenAssignmentDialog] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [expandedAssignment, setExpandedAssignment] = useState<string>('');
    const [latestEvent, setLatestEvent] = useState<EventDto>(viewEvent);
    const [confirmDeleteButtonListByAssignmentIndex, setConfirmDeleteButtonListByAssignmentIndex] = useState<boolean[]>([])

    const handleConfirmDeleteButton = (index: number) => {
        let tempArray = [...confirmDeleteButtonListByAssignmentIndex]
        tempArray[index] = !tempArray[index]
        setConfirmDeleteButtonListByAssignmentIndex(tempArray)
    }

    const handleResetConfirmDeleteButtons = () => {
        setConfirmDeleteButtonListByAssignmentIndex([])
    }

    const handleListChange = (assignmentId: string) => {
        const isExpanding = expandedAssignment !== assignmentId;
        setExpandedAssignment(isExpanding ? assignmentId : '');
        if (isExpanding) {
            const assignment = assignments.find(a => a.id === assignmentId);
            if (assignment) {
                const localStorageKey = `assignment-update-${viewEvent.id}-${assignment.id}`;
                localStorage.setItem(localStorageKey, assignment.changedDate.toString());
            }
        }
    }
    const handleOpenAssignmentDialog = () => {
        setOpenAssignmentDialog(true);
    }

    const handleCloseAssignmentDialog = () => {
        setOpenAssignmentDialog(false)
    }

    const fetchAssignmentsForEvent = async (event: EventDto) => {
        setAssignments([]);
        setIsLoading(true);
        let additionalRelatedAssignmentsQuery;
        if (event.additionalRelatedAssignments?.length > 0) {
            additionalRelatedAssignmentsQuery = "&relatedAssignmentIds=" + event.additionalRelatedAssignments?.join(';');
        }
        const response = await context.restApiClient
            .buildRequest(
                `/api/assignment?eventId=${event.id}${additionalRelatedAssignmentsQuery || ''}`
            )
            .deserializeResponseToJson<Assignment[]>()
            .getAsync();
        if (response.json) {
            setAssignments(response.json);
            setIsLoading(false);
            return;
        }
        if (response.error) {
            enqueueSnackbar(
                <Typography variant="body2">Kunne ikke hente oppdrag. Prøv igjen eller kontakt systemansvarlig.</Typography>,
                { variant: "error" }
            );
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    };

    const handleOnChange = async () => {
        const updatedEvent = await onUpdateEventView(viewEvent.id);
        if (updatedEvent) {
            setLatestEvent(updatedEvent as EventDto);
            await fetchAssignmentsForEvent(updatedEvent as EventDto);
        }
    };
    
    useEffect(() => {
        (async () => {
            await fetchAssignmentsForEvent(latestEvent);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

        const removeRelatedAssignmentIdToEvent = async (assignmentId: string) => {
            setAssignments([]);
            setIsLoading(true);

            const response = await context.restApiClient
                .buildRequest(`/api/events/${viewEvent.id}/assignment?relatedAssignmentId=${assignmentId.toUpperCase()}&action=remove`)
                .putAsync();

            if (response.error) {
                enqueueSnackbar("Kunne ikke fjerne oppdraget", { variant: "error" });
                setIsLoading(false);
            } 
            else {
                enqueueSnackbar(`Oppdrag ${assignmentId.toUpperCase()} fjernet`, {
                    variant: "success",
                });

                await handleOnChange();
                setIsLoading(false);
            }
        };



    return (
        <>
            <AccordionSetup
                title="Oppdrag"
                contentHeight={400}
                icon={<AssignmentOutlinedIcon />}
                actionIcon={<AddCircleOutlineOutlined />}
                action={handleOpenAssignmentDialog}
            >
                {isLoading && assignments.length === 0 &&
                    <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                        <LoadingIndicator size={'small'} />
                    </Box>
                }
                {!isLoading && assignments.length === 0 &&
                    <Typography variant="body2">Ingen oppdrag.</Typography>
                }
                    {assignments.sort((a, b) => new Date(b.createdDate as Date).getTime() - new Date(a.createdDate as Date).getTime()).map((assignment, index) => {
                        const localStorageKey = `assignment-update-${viewEvent.id}-${assignment.id}`;
                        const lastChecked = localStorage.getItem(localStorageKey);
                        const hasUpdate = !!lastChecked && new Date(assignment.changedDate) > new Date(lastChecked);
                        return (
                            <Stack key={assignment.id} spacing={1} sx={{ mb: index < assignments.length - 1 ? 1 : 0 }}>
                                <Box 
                                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                                    onClick={() => handleListChange(assignment.id)}
                                >
                                    <Stack direction={"row"} spacing={1} sx={{
                                        alignItems: "center"
                                    }}>
                                        <Typography 
                                            noWrap 
                                            variant="body2" 
                                            onClick={(e) => { e.stopPropagation(); }} 
                                            sx={{
                                                alignContent: "center"
                                            }}
                                        >
                                            <SafeLink 
                                                onClick={(e) => { e.stopPropagation(); }} 
                                                href={"https://oppdrag.digital.eidsiva.no/assignment/" + assignment.id}
                                            >
                                                <b>{assignment.id}</b>
                                            </SafeLink>
                                            {hasUpdate && (
                                                <Tooltip title="Oppdraget er oppdatert siden sist du sjekket">
                                                    <NotificationsActiveOutlined color="warning" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                                </Tooltip>
                                            )}
                                        </Typography>
                                    </Stack>
                                    <Stack
                                        direction={"row"}
                                        spacing={1}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            width: !latestEvent.additionalRelatedAssignments?.includes(assignment.id.toUpperCase()) ? "50%" : "30%",
                                            alignItems: "center"
                                        }}>
                                        <AssignmentChip status={assignment.status.value}/>
                                        {latestEvent.additionalRelatedAssignments?.includes(assignment.id.toUpperCase()) && 
                                            <Stack direction="row">
                                                {confirmDeleteButtonListByAssignmentIndex[index] && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            await removeRelatedAssignmentIdToEvent(assignment.id);
                                                            handleResetConfirmDeleteButtons()
                                                        }}
                                                    >
                                                        <CheckOutlined color="success" />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleConfirmDeleteButton(index); }}
                                                >
                                                    {confirmDeleteButtonListByAssignmentIndex[index] ? (
                                                        <CloseOutlined color="error" />
                                                    ) : (
                                                        <Tooltip title="Fjern tilleggsoppdrag">
                                                            <DeleteOutlined color="error" />
                                                        </Tooltip>
                                                    )}
                                                </IconButton>
                                            </Stack>
                                        }
                                        <IconButton
                                            size="small"
                                            onClick={() => handleListChange(assignment.id)}
                                        >
                                            {
                                                expandedAssignment === assignment.id 
                                                ? <ExpandLessOutlined /> 
                                                : <ExpandMoreOutlined /> 
                                            }
                                        </IconButton>
                                    </Stack>
                                </Box>
                                <Collapse in={expandedAssignment === assignment.id} timeout="auto" unmountOnExit>
                                    <Stack spacing={1} sx={{ p: 1, minWidth: 0 }}>
                                        <Stack direction={"row"} sx={{
                                            justifyContent: "space-between"
                                        }}>
                                            <Typography variant="body2"><b>Opprettet av:</b></Typography>
                                            <Typography variant="body2" sx={{
                                                textAlign: "right"
                                            }}>{assignment.createdBy}</Typography>
                                        </Stack>
                                        <Stack direction={"row"} sx={{
                                            justifyContent: "space-between"
                                        }}>
                                            <Typography variant="body2"><b>Kilde:</b></Typography>
                                            <Typography variant="body2" sx={{
                                                textAlign: "right"
                                            }}>{assignment.source.value}</Typography>
                                        </Stack>
                                        <Stack direction={"row"} sx={{
                                            justifyContent: "space-between"
                                        }}>
                                            <Typography  variant="body2"><b>Opprettet:</b></Typography>
                                            <Typography  variant="body2" sx={{
                                                textAlign: "right"
                                            }}>{new Date(assignment.createdDate as Date).toLocaleString("nb-NO")}</Typography>
                                        </Stack>
                                        <Stack direction={"row"} sx={{
                                            justifyContent: "space-between"
                                        }}>
                                            <Typography variant="body2"><b>Sist endret:</b></Typography>
                                            <Typography variant="body2" sx={{
                                                textAlign: "right"
                                            }}>{new Date(assignment.changedDate as Date).toLocaleString("nb-NO")}</Typography>
                                        </Stack>
                                        <Stack direction={"row"} sx={{
                                            justifyContent: "space-between"
                                        }}>
                                            <Typography variant="body2"><b>Entreprenør:</b></Typography>
                                            <Typography variant="body2" sx={{
                                                textAlign: "right"
                                            }}>{assignment.entrepreneurName}</Typography>
                                        </Stack>
                                    </Stack>
                                </Collapse>
                                {index < assignments.length - 1 && <Divider />}
                            </Stack>
                        );})}
            </AccordionSetup>
            <CreateOrAddAssignment 
                eventId={viewEvent.id}
                isOpen={openAssignmentDialog}
                onClose={handleCloseAssignmentDialog}
                onChange={handleOnChange}
            />
        </>
    );
}

export default AssignmentContainer;